'use client';

/**
 * App Navbar Component
 *
 * Navbar for app sections (chat, create, generate, etc.)
 * Shows authenticated UI when user is signed in, otherwise shows Sign Up/Login buttons.
 *
 * Mobile: [SidebarToggle] [Logo] ---- [Coins] [Profile]
 * Desktop: [Search] ---- [Nav Links] ---- [Coins] [Profile] [SignOut]
 */
import { useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { useQueryClient } from '@tanstack/react-query';
import { LogOut, Menu, Search, UserCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useSignOut } from '@kit/supabase/hooks/use-sign-out';
import { useUser } from '@kit/supabase/hooks/use-user';
import { Trans } from '@kit/ui/trans';

import { useAuthModal } from '~/components/auth/auth-modal-context';
import { CoinBalance } from '~/components/wallet/coin-balance';
import { FirstPurchasePill } from '~/components/wallet/first-purchase-pill';

import { useSidebar } from './sidebar-context';

interface AppNavbarProps {
  showCoinBalance?: boolean;
}

export function AppNavbar({ showCoinBalance = true }: AppNavbarProps) {
  const { t } = useTranslation('common');
  const [searchQuery, setSearchQuery] = useState('');
  const router = useRouter();
  const signOut = useSignOut();
  const { data: user } = useUser();
  const { openAuthModal } = useAuthModal();
  const { isOpen, setMobileOpen, toggleSidebar, sidebarWidth, isAnimating } =
    useSidebar();
  const queryClient = useQueryClient();

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) {
      router.push(`/catalog?q=${encodeURIComponent(q)}`);
      setSearchQuery('');
    }
  };

  const handleSignOut = async () => {
    // Clear caches immediately so navbar updates even if page reload is slow (mobile)
    queryClient.setQueryData(['supabase:user'], undefined);
    queryClient.removeQueries({ queryKey: ['wallet:balance'] });

    await signOut.mutateAsync();
  };

  const isAuthenticated = !!user;

  return (
    <nav
      className="fixed top-[var(--promo-banner-h,0px)] right-0 left-0 z-[120] h-16 overflow-x-hidden border-b border-white/10"
      style={{ background: '#0c0c0f' }}
    >
      <div className="flex h-full w-full max-w-full items-center px-2 md:px-3 lg:px-4">
        <div className="flex h-full w-full items-center justify-between gap-2 md:gap-3">
          {/* Mobile: Sidebar Toggle + Logo */}
          <div className="flex items-center gap-2 lg:hidden">
            <button
              onClick={() => setMobileOpen(true)}
              className="hover:text-eros-pink rounded-lg p-2 text-white transition-colors hover:bg-white/5"
              aria-label={t('navbar.openSidebarAria')}
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link href="/" className="flex items-center gap-1.5">
              <Image
                src="/images/favicon/logo.webp"
                alt="Edem AI"
                width={32}
                height={32}
                className="h-8 w-8 flex-shrink-0"
                priority
              />
              <span className="text-lg tracking-wide">
                <span className="font-bold text-white">Edem</span>
                <span className="font-light text-white/70"> AI</span>
              </span>
            </Link>
          </div>

          {/* Desktop: Sidebar toggle + logo — locked to the sidebar width so the
              right border lines up with the sidebar's border. */}
          <div
            className="hidden h-full flex-shrink-0 items-center gap-2.5 overflow-hidden border-r border-white/10 lg:flex"
            style={{
              // Open: width = sidebar − 16 (navbar padding), left-aligned.
              // Collapsed: span the full sidebar width and center the burger so
              // it lines up on the same axis as the collapsed sidebar icons.
              width: isOpen ? `${sidebarWidth - 16}px` : `${sidebarWidth}px`,
              marginLeft: isOpen ? undefined : '-16px',
              justifyContent: isOpen ? undefined : 'center',
              transition: isAnimating
                ? 'width 300ms cubic-bezier(0.4, 0, 0.2, 1), margin-left 300ms cubic-bezier(0.4, 0, 0.2, 1)'
                : undefined,
            }}
          >
            <button
              onClick={toggleSidebar}
              className="rounded-lg p-2 text-white transition-colors hover:bg-white/10"
              aria-label="Toggle sidebar"
            >
              <Menu className="h-5 w-5" />
            </button>
            {isOpen && (
              <Link href="/" className="flex items-center gap-2">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/images/favicon/logo-64.webp"
                  width={64}
                  height={64}
                  alt="Edem AI"
                  className="h-8 w-8 flex-shrink-0"
                />
                <span
                  className="text-2xl leading-none font-semibold tracking-tight whitespace-nowrap"
                  style={{ fontFamily: 'var(--font-display), Georgia, serif' }}
                >
                  <span className="text-white">Edem</span>{' '}
                  <span style={{ color: '#b07cf0' }}>AI</span>
                </span>
              </Link>
            )}
          </div>

          {/* Desktop: Search Component - Left side */}
          <form
            onSubmit={handleSearchSubmit}
            className="relative hidden w-auto max-w-[340px] min-w-[200px] lg:block xl:max-w-[420px]"
          >
            <div className="group relative">
              <Search className="group-focus-within:text-eros-pink absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-gray-400 transition-colors" />
              <input
                type="text"
                placeholder={t('navbar.searchCharactersPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="focus:border-eros-pink/50 w-full rounded-xl border border-white/10 bg-transparent py-2 pr-8 pl-10 text-sm text-white placeholder-gray-500 transition-all focus:bg-black/10 focus:shadow-[0_0_20px_rgba(236,72,153,0.15)] focus:outline-none"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute top-1/2 right-3 -translate-y-1/2 text-gray-400 transition-colors hover:text-white"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </form>

          {/* Spacer — nav links removed; pushes the auth section to the right. */}
          <div className="hidden flex-1 lg:block" />

          {/* Auth Section - Desktop */}
          <div className="ml-2 hidden flex-shrink-0 items-center gap-2 border-l border-white/20 pl-2 lg:ml-3 lg:flex lg:pl-3 xl:ml-6 xl:gap-3 xl:pl-6">
            {isAuthenticated ? (
              <>
                <FirstPurchasePill />
                {showCoinBalance && <CoinBalance />}
                <Link
                  href="/account"
                  className="from-eros-pink to-eros-purple rounded-xl bg-gradient-to-r px-2 py-2 text-[11px] font-semibold whitespace-nowrap text-white transition-all hover:shadow-[0_0_20px_rgba(236,72,153,0.4)] lg:px-3 lg:text-xs xl:px-5 xl:text-sm"
                >
                  <Trans i18nKey="common:navbar.myProfile" />
                </Link>
                <button
                  onClick={handleSignOut}
                  className="rounded-xl border border-white/20 p-2 text-white transition-all hover:border-red-500 hover:text-red-400 hover:shadow-[0_0_15px_rgba(239,68,68,0.2)]"
                  title={t('navbar.signOutAria')}
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => openAuthModal('sign-in')}
                  className="hover:border-eros-pink hover:text-eros-pink rounded-xl border border-white/20 px-2 py-2 text-[11px] whitespace-nowrap text-white transition-all hover:shadow-[0_0_15px_rgba(236,72,153,0.2)] lg:px-3 lg:text-xs xl:px-5 xl:text-sm"
                >
                  <Trans i18nKey="common:navbar.login" />
                </button>
                <button
                  onClick={() => openAuthModal('sign-up')}
                  className="from-eros-pink to-eros-purple rounded-xl bg-gradient-to-r px-2 py-2 text-[11px] font-semibold whitespace-nowrap text-white transition-all hover:shadow-[0_0_20px_rgba(236,72,153,0.4)] lg:px-3 lg:text-xs xl:px-5 xl:text-sm"
                >
                  <Trans i18nKey="common:navbar.signUp" />
                </button>
              </>
            )}
          </div>

          {/* Mobile: Auth Section (right side) */}
          <div className="flex items-center gap-1.5 lg:hidden">
            {isAuthenticated ? (
              <>
                <FirstPurchasePill />
                {showCoinBalance && <CoinBalance showBuyButton={false} />}
                <Link
                  href="/account"
                  className="hover:border-eros-pink hover:text-eros-pink rounded-lg border border-white/20 p-1.5 text-white transition-all"
                  title={t('navbar.myAccountAria')}
                >
                  <UserCircle className="h-4 w-4" />
                </Link>
                <button
                  onClick={handleSignOut}
                  className="rounded-lg border border-white/20 p-1.5 text-white transition-all hover:border-red-500 hover:text-red-400"
                  title={t('navbar.signOutAria')}
                >
                  <LogOut className="h-3.5 w-3.5" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => openAuthModal('sign-in')}
                  className="hover:border-eros-pink rounded-xl border border-white/20 px-2.5 py-1.5 text-[10px] whitespace-nowrap text-white transition-colors md:text-xs"
                >
                  <Trans i18nKey="common:navbar.login" />
                </button>
                <button
                  onClick={() => openAuthModal('sign-up')}
                  className="from-eros-pink to-eros-purple rounded-xl bg-gradient-to-r px-2.5 py-1.5 text-[10px] font-semibold whitespace-nowrap text-white transition-all md:text-xs"
                >
                  <Trans i18nKey="common:navbar.signUp" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}
